import { PLATFORM_TENANT, toPrincipal } from '@platform/authz';
import { chooseInitialTenant, readActiveTenant, writeActiveTenant } from "../auth/active-tenant.js";
import { withActiveTenant } from "../auth/principal.js";
import { AuthFlowError } from "../auth/types.js";
import { MOCK_EMAIL_CODE, MOCK_TOTP_CODE, MOCK_TOTP_SECRET, membershipsOf, personByEmail, personOf, tenantOf, } from "./world.js";
const SESSION_KEY = 'sprocket.mockSession';
/**
 * Mock auth (`mode: mock`, ADR-0012): the same `AuthAdapter` as Cognito, driven by the mock
 * world. It answers the same challenges — an invited person gets `new-password-required`, an
 * enrolled person `totp-code`, staff without an authenticator `totp-setup`, a self-signed-up
 * person who never entered the emailed code `confirm-sign-up` — so every screen can be walked
 * with no backend. Self sign-up works only in a world whose pool takes it (a personal product). The session survives a reload via `sessionStorage`
 * (tab-scoped); the real adapter keeps tokens in memory instead.
 */
export function createMockAuth(options) {
    const { world, audience } = options;
    let current = null;
    let pending = null;
    const persist = (sub) => {
        try {
            if (sub)
                sessionStorage.setItem(SESSION_KEY, sub);
            else
                sessionStorage.removeItem(SESSION_KEY);
        }
        catch {
            // Storage blocked: the mock session lives only in memory.
        }
    };
    const build = (person, tenantHint) => {
        if (audience === 'platform') {
            const base = toPrincipal({
                sub: person.sub,
                tenantId: PLATFORM_TENANT,
                tier: 'pool',
                memberships: [],
                roles: person.platformAdmin ? ['PlatformAdmin'] : [],
                grants: [],
            });
            return { ...base, name: person.name, email: person.email, workspaces: [] };
        }
        const workspaces = membershipsOf(world, person.sub).map((m) => {
            const tenant = tenantOf(world, m.tenantId);
            return {
                tenantId: m.tenantId,
                roles: [...m.roles],
                ...(m.officeCode ? { officeCode: m.officeCode } : {}),
                ...(m.agentCode ? { agentCode: m.agentCode } : {}),
                grants: m.grants,
                tenantName: tenant?.name ?? m.tenantId,
                tier: tenant?.tier ?? 'pool',
                ...(tenant?.brand ? { brand: tenant.brand } : {}),
            };
        });
        const anonymous = toPrincipal({
            sub: person.sub,
            tier: 'pool',
            memberships: workspaces.map((w) => ({
                tenantId: w.tenantId,
                roles: w.roles,
                grants: w.grants,
            })),
            roles: [],
            grants: [],
        });
        const principal = {
            ...anonymous,
            name: person.name,
            email: person.email,
            workspaces,
        };
        const tenantId = chooseInitialTenant(workspaces.map((w) => w.tenantId), { tenantHint, remembered: readActiveTenant() });
        return tenantId ? withActiveTenant(principal, tenantId) : principal;
    };
    const finish = (person, tenantHint) => {
        current = build(person, tenantHint);
        persist(person.sub);
        if (current.tenantId && audience === 'tenant')
            writeActiveTenant(current.tenantId);
        pending = null;
        return { kind: 'done', principal: current };
    };
    const stepFor = (person, tenantHint) => {
        const hint = tenantHint ? { tenantHint } : {};
        if (person.unconfirmed)
            return { kind: 'confirm-sign-up' };
        if (person.invited) {
            pending = { challenge: 'new-password', sub: person.sub, ...hint };
            return { kind: 'new-password-required' };
        }
        if (person.totpEnrolled) {
            pending = { challenge: 'totp-code', sub: person.sub, ...hint };
            return { kind: 'totp-code' };
        }
        if (audience === 'platform') {
            // The platform-admin pool requires TOTP: enrol before the first session.
            pending = { challenge: 'totp-setup', sub: person.sub, ...hint };
            return {
                kind: 'totp-setup',
                secret: MOCK_TOTP_SECRET,
                uri: totpUri(options.appName, person.email),
            };
        }
        return finish(person, tenantHint);
    };
    const eligible = (person) => (audience === 'platform' ? person.staff : !person.staff);
    const needPending = (challenge) => {
        if (!pending || pending.challenge !== challenge)
            throw new AuthFlowError('no-session', 'Start by signing in again.');
        const person = personOf(world, pending.sub);
        if (!person)
            throw new AuthFlowError('no-session', 'Start by signing in again.');
        return { person, tenantHint: pending.tenantHint };
    };
    return {
        mode: 'mock',
        fixtures: () => world.people.filter(eligible).map((p) => ({
            id: p.sub,
            label: p.name,
            description: describe(world, p, audience),
            email: p.email,
            password: p.password,
        })),
        signInAsFixture: (fixtureId) => {
            const person = personOf(world, fixtureId);
            if (!person || !eligible(person))
                return Promise.reject(new AuthFlowError('invalid-credentials', 'No such fixture'));
            // Fixture buttons skip every challenge, the emailed code included.
            person.unconfirmed = false;
            return Promise.resolve(finish(person));
        },
        restore: () => {
            let sub;
            try {
                sub = sessionStorage.getItem(SESSION_KEY);
            }
            catch {
                sub = null;
            }
            const person = sub ? personOf(world, sub) : undefined;
            current = person && eligible(person) && !person.unconfirmed ? build(person) : null;
            return Promise.resolve(current);
        },
        signIn: ({ email, password, tenantHint }) => {
            pending = null;
            const person = personByEmail(world, email);
            if (!person || !eligible(person) || person.password !== password)
                return Promise.reject(new AuthFlowError('invalid-credentials', 'The email or password is not right.'));
            return Promise.resolve(stepFor(person, tenantHint));
        },
        completeNewPassword: (newPassword) => {
            const { person, tenantHint } = needPending('new-password');
            if (!isStrong(newPassword))
                return Promise.reject(new AuthFlowError('password-policy', 'Use at least 12 characters with upper and lower case, a number and a symbol.'));
            person.password = newPassword;
            person.invited = false;
            for (const m of membershipsOf(world, person.sub))
                m.status = 'active';
            for (const inv of world.invitations)
                if (inv.email.toLowerCase() === person.email.toLowerCase() && inv.status === 'pending')
                    inv.status = 'accepted';
            pending = null;
            return Promise.resolve(stepFor({ ...person, invited: false }, tenantHint));
        },
        confirmTotp: (code) => {
            const { person, tenantHint } = needPending('totp-code');
            if (code.trim() !== MOCK_TOTP_CODE)
                return Promise.reject(new AuthFlowError('invalid-code', 'That code is not right. Check the app and try again.'));
            return Promise.resolve(finish(person, tenantHint));
        },
        beginTotpSetup: () => {
            const email = current?.email ?? (pending ? personOf(world, pending.sub)?.email : undefined);
            if (!email)
                return Promise.reject(new AuthFlowError('no-session', 'Sign in first'));
            return Promise.resolve({ secret: MOCK_TOTP_SECRET, uri: totpUri(options.appName, email) });
        },
        confirmTotpSetup: (code) => {
            if (code.trim() !== MOCK_TOTP_CODE)
                return Promise.reject(new AuthFlowError('invalid-code', 'That code is not right. Check the app and try again.'));
            if (pending?.challenge === 'totp-setup') {
                const { person, tenantHint } = needPending('totp-setup');
                person.totpEnrolled = true;
                return Promise.resolve(finish(person, tenantHint));
            }
            if (!current)
                return Promise.reject(new AuthFlowError('no-session', 'Sign in first'));
            const person = personOf(world, current.sub);
            if (person)
                person.totpEnrolled = true;
            return Promise.resolve({ kind: 'done', principal: current });
        },
        forgotPassword: (email) => {
            const person = personByEmail(world, email);
            // Cognito hides whether the account exists (preventUserExistenceErrors); so does the mock.
            return Promise.resolve({ destination: person ? maskEmail(person.email) : null });
        },
        resetPassword: ({ email, code, newPassword }) => {
            const person = personByEmail(world, email);
            if (!person || code.trim() !== MOCK_TOTP_CODE)
                return Promise.reject(new AuthFlowError('invalid-code', 'That code is not right. Check your email and try again.'));
            if (!isStrong(newPassword))
                return Promise.reject(new AuthFlowError('password-policy', 'Use at least 12 characters with upper and lower case, a number and a symbol.'));
            person.password = newPassword;
            person.invited = false;
            return Promise.resolve();
        },
        changePassword: ({ currentPassword, newPassword }) => {
            if (!current)
                return Promise.reject(new AuthFlowError('no-session', 'Sign in first'));
            const person = personOf(world, current.sub);
            if (!person || person.password !== currentPassword)
                return Promise.reject(new AuthFlowError('invalid-credentials', 'The current password is not right.'));
            if (!isStrong(newPassword))
                return Promise.reject(new AuthFlowError('password-policy', 'Use at least 12 characters with upper and lower case, a number and a symbol.'));
            person.password = newPassword;
            return Promise.resolve();
        },
        signUp: ({ email, password, givenName, familyName }) => {
            if (!world.selfSignUp || audience !== 'tenant')
                return Promise.reject(new AuthFlowError('not-authorized', 'New accounts cannot be created here.'));
            if (personByEmail(world, email))
                return Promise.reject(new AuthFlowError('account-exists', 'An account with this email already exists. Sign in instead.'));
            if (!isStrong(password))
                return Promise.reject(new AuthFlowError('password-policy', 'Use at least 12 characters with upper and lower case, a number and a symbol.'));
            const normalised = email.trim().toLowerCase();
            const name = [givenName?.trim(), familyName?.trim()].filter(Boolean).join(' ');
            world.people.push({
                sub: `mock|${world.nextId('u')}`,
                email: normalised,
                name: name || normalised,
                password,
                invited: false,
                totpEnrolled: false,
                staff: false,
                platformAdmin: false,
                unconfirmed: true,
            });
            return Promise.resolve({ kind: 'confirm', destination: maskEmail(normalised) });
        },
        confirmSignUp: ({ email, code }) => {
            const person = personByEmail(world, email);
            // Like the pool (preventUserExistenceErrors): an unknown address is just a wrong code.
            if (person && !person.unconfirmed)
                return Promise.reject(new AuthFlowError('already-confirmed', 'This email is already confirmed. Sign in to continue.'));
            if (!person || code.trim() !== MOCK_EMAIL_CODE)
                return Promise.reject(new AuthFlowError('invalid-code', 'That code is not right. Check the email and try again.'));
            person.unconfirmed = false;
            return Promise.resolve();
        },
        resendSignUpCode: (email) => {
            const person = personByEmail(world, email);
            if (person && !person.unconfirmed)
                return Promise.reject(new AuthFlowError('already-confirmed', 'This email is already confirmed. Sign in to continue.'));
            return Promise.resolve({ destination: person ? maskEmail(person.email) : null });
        },
        refresh: () => {
            // The mock's "new tokens": rebuild from the world, so a membership added since sign-in shows.
            const person = current ? personOf(world, current.sub) : undefined;
            if (!person) {
                current = null;
                return Promise.resolve(null);
            }
            current = build(person);
            if (current.tenantId && audience === 'tenant')
                writeActiveTenant(current.tenantId);
            return Promise.resolve(current);
        },
        signOut: () => {
            current = null;
            pending = null;
            persist(null);
            writeActiveTenant(null);
            return Promise.resolve();
        },
        switchTenant: (tenantId) => {
            if (!current)
                return Promise.reject(new AuthFlowError('no-session', 'Not signed in'));
            try {
                current = withActiveTenant(current, tenantId);
            }
            catch {
                return Promise.reject(new AuthFlowError('not-a-member', 'You are not a member of that workspace'));
            }
            writeActiveTenant(tenantId);
            return Promise.resolve(current);
        },
        getAccessToken: () => Promise.resolve(current ? `mock.${current.sub}` : null),
    };
}
function describe(world, p, audience) {
    if (p.blurb)
        return p.blurb;
    if (audience === 'platform') {
        const role = p.platformAdmin ? 'Platform admin' : 'Staff without a platform role';
        return `${role}${p.totpEnrolled ? ' · authenticator enrolled' : ' · no authenticator yet'}`;
    }
    const parts = membershipsOf(world, p.sub).map((m) => {
        const tenant = tenantOf(world, m.tenantId);
        const role = m.roles[0] ?? 'Member';
        return `${ROLE_LABEL[role] ?? role} at ${tenant?.name ?? m.tenantId}${m.status === 'invited' ? ' (invited)' : ''}`;
    });
    return parts.join(' · ');
}
const ROLE_LABEL = {
    TenantAdmin: 'Admin',
    Manager: 'Manager',
    Agent: 'Agent',
    PlatformAdmin: 'Platform admin',
};
function totpUri(appName = 'Sprocket', email) {
    return `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(email)}?secret=${MOCK_TOTP_SECRET}&issuer=${encodeURIComponent(appName)}`;
}
function maskEmail(email) {
    const [user = '', domain = ''] = email.split('@');
    return `${user.slice(0, 1)}***@${domain}`;
}
/** The pool's password policy (12+, upper, lower, digit, symbol), mirrored so the mock says no too. */
export function isStrong(password) {
    return (password.length >= 12 &&
        /[a-z]/.test(password) &&
        /[A-Z]/.test(password) &&
        /\d/.test(password) &&
        /[^A-Za-z0-9]/.test(password));
}
//# sourceMappingURL=mock-auth.js.map